import { useRef, useState, useEffect } from "react";
import { ImageIcon, Upload, Loader2, Trash2, Type, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, fileUrl } from "@/lib/api";
import { useSettings, DEFAULT_HERO_TITLE, DEFAULT_HERO_SUBTITLE } from "@/context/SettingsContext";
import { ImageCropModal } from "@/components/ImageCropModal";

const defaultHero =
  "https://images.unsplash.com/photo-1729556227126-452c221f1ef4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const ImagePicker = ({ label, hint, current, aspect, cropAspect, onUpload, onReset, fallback, testid, emptyText = "No image — the text logo is used" }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", blob, "image.jpg");
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onUpload(data.paths[0]);
      toast.success(`${label} updated`);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Session expired — please log in again" : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    if (cropAspect) {
      const reader = new FileReader();
      reader.onload = () => setCropSrc(reader.result);
      reader.readAsDataURL(file);
    } else {
      await uploadBlob(file);
    }
  };

  const preview = current ? fileUrl(current) : fallback;

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <ImageIcon size={16} className="text-red-500" />
        <h3 className="font-display font-bold text-sm">{label}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">{hint}</p>
      {preview ? (
        <div className={`relative ${aspect} rounded-lg overflow-hidden border border-slate-700 mb-3 bg-slate-900`}>
          <img src={preview} alt="preview" className="w-full h-full object-contain" data-testid={`${testid}-preview`} />
          {!current && (
            <span className="absolute top-2 left-2 bg-slate-900/80 text-slate-300 text-[10px] font-semibold px-2 py-1 rounded">
              Default
            </span>
          )}
        </div>
      ) : (
        <div className={`${aspect} rounded-lg border border-dashed border-slate-700 mb-3 flex items-center justify-center text-slate-500 text-xs`}>
          {emptyText}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid={`${testid}-input`} />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => inputRef.current?.click()} disabled={uploading}
          className="bg-red-600 hover:bg-red-700 font-semibold rounded-full" data-testid={`${testid}-upload`}>
          {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
          {current ? "Replace" : "Upload"}
        </Button>
        {current && (
          <Button variant="outline" onClick={onReset} disabled={uploading}
            className="rounded-full border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white" data-testid={`${testid}-reset`}>
            <Trash2 size={16} className="mr-2" /> Use Default
          </Button>
        )}
      </div>
      {cropAspect && (
        <ImageCropModal
          open={!!cropSrc}
          imageSrc={cropSrc}
          aspect={cropAspect}
          onCancel={() => setCropSrc(null)}
          onConfirm={async (blob) => { setCropSrc(null); await uploadBlob(blob); }}
        />
      )}
    </div>
  );
};

const PersonEditor = ({ prefix, label, settings, put, namePlaceholder, titlePlaceholder }) => {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const savedName = settings?.[`${prefix}_name`];
  const savedTitle = settings?.[`${prefix}_title`];

  useEffect(() => {
    setName(savedName || "");
    setTitle(savedTitle || "");
  }, [savedName, savedTitle]);

  const save = async () => {
    setSaving(true);
    try {
      await put({ [`${prefix}_name`]: name.trim() || null, [`${prefix}_title`]: title.trim() || null });
      toast.success(`${label} info saved — it's live on your home page`);
    } catch {
      toast.error(`Could not save ${label.toLowerCase()} info`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-slate-700 pt-6 space-y-3">
      <ImagePicker
        label={`${label} Photo`}
        hint={`Upload a photo for the ${label} spot in the Meet Our Team section on your home page.`}
        current={settings?.[`${prefix}_photo`]}
        aspect="aspect-square max-w-[220px]"
        cropAspect={1}
        fallback={null}
        emptyText="No photo yet"
        testid={`${prefix}-photo`}
        onUpload={(path) => put({ [`${prefix}_photo`]: path })}
        onReset={async () => { await put({ [`${prefix}_photo`]: null }); toast.success(`${label} photo removed`); }}
      />
      <div>
        <Label className="text-xs font-semibold text-slate-400">Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-slate-900 border-slate-700 text-white"
          placeholder={namePlaceholder} data-testid={`${prefix}-name-input`} />
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-400">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-slate-900 border-slate-700 text-white"
          placeholder={titlePlaceholder} data-testid={`${prefix}-title-input`} />
      </div>
      <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 font-semibold rounded-full" data-testid={`${prefix}-save`}>
        {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />} Save {label} Info
      </Button>
    </div>
  );
};

export const SiteSettings = () => {
  const { settings, refresh } = useSettings() || { settings: {}, refresh: () => {} };
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [savingText, setSavingText] = useState(false);

  useEffect(() => {
    setTitle(settings?.hero_title || "");
    setSubtitle(settings?.hero_subtitle || "");
  }, [settings?.hero_title, settings?.hero_subtitle]);

  const put = async (payload) => {
    await api.put("/admin/settings", payload);
    await refresh();
  };

  const saveText = async () => {
    setSavingText(true);
    try {
      await put({ hero_title: title.trim() || null, hero_subtitle: subtitle.trim() || null });
      toast.success("Hero text saved — it's live on your home page");
    } catch {
      toast.error("Could not save text");
    } finally {
      setSavingText(false);
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 mb-8 space-y-8" data-testid="site-settings">
      <ImagePicker
        label="Home Page Hero Image"
        hint="Upload your own photo, then drag to reposition and crop it to fit the hero banner."
        current={settings?.hero_image}
        aspect="aspect-[16/6]"
        cropAspect={16 / 6}
        fallback={defaultHero}
        testid="hero-image"
        onUpload={(path) => put({ hero_image: path })}
        onReset={async () => { await put({ hero_image: null }); toast.success("Reverted to default hero"); }}
      />

      <div className="border-t border-slate-700 pt-6">
        <div className="flex items-center gap-2 mb-1">
          <Type size={16} className="text-red-500" />
          <h3 className="font-display font-bold text-sm">Hero Text</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Edit the big headline and the line beneath it. Leave blank to use the default.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-slate-400">Headline</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-slate-900 border-slate-700 text-white"
              placeholder={DEFAULT_HERO_TITLE} data-testid="hero-title-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-400">Subtitle</Label>
            <Textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={3} className="mt-1 bg-slate-900 border-slate-700 text-white"
              placeholder={DEFAULT_HERO_SUBTITLE} data-testid="hero-subtitle-input" />
          </div>
          <Button onClick={saveText} disabled={savingText} className="bg-red-600 hover:bg-red-700 font-semibold rounded-full" data-testid="hero-text-save">
            {savingText ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />} Save Text
          </Button>
        </div>
      </div>

      <div className="border-t border-slate-700 pt-6">
        <ImagePicker
          label="Dealership Logo"
          hint="Upload your logo to replace the car icon + name in the top navigation bar. Transparent PNG works best."
          current={settings?.logo_image}
          aspect="aspect-[16/5]"
          fallback={null}
          testid="logo-image"
          onUpload={(path) => put({ logo_image: path })}
          onReset={async () => { await put({ logo_image: null }); toast.success("Reverted to text logo"); }}
        />
      </div>

      <PersonEditor prefix="owner" label="Owner" settings={settings} put={put} namePlaceholder="e.g. Wayne Erskine" titlePlaceholder="e.g. Owner" />
      <PersonEditor prefix="sales" label="Sales" settings={settings} put={put} namePlaceholder="e.g. Steve Smith" titlePlaceholder="e.g. Sales" />
    </div>
  );
};
